
import { useEffect, useState } from "react";
import axios from "axios";
import { apiKey } from "../../../App";

export interface Usedbo<T> {
  get: T[] | undefined;
  set: React.Dispatch<React.SetStateAction<T[] | undefined>>;
  error: any;
  loading: boolean;
  add: (item: T) => void;
  remove: (id: string | (T & { _id: string })) => void;
  update: (item: T & { _id: string }) => void;
  updateById: (id: string, item: Partial<T>) => void;
  updateId: (item: T & { id: string }) => void;
  filter: (fn: (item: T) => boolean) => T[];
  log: () => void;
  sort: (fn: (a: T, b: T) => number) => T[];
  sortBy: (field: keyof T, direction?: number) => T[];
  refresh: () => Promise<void>;
  reduce: (field: keyof T) => number;
  uniqueSet: (field: keyof T) => any[];
  map: <U>(fn: (item: T) => U) => U[];
  find: (fn: (item: T) => boolean) => T | undefined;
  findById: (id: string) => T | undefined;
  replace: (item: T & { _id: string }) => void;
  length: () => number;
}

/**
 * Usedbo<T>
 *
 * A client-side data access helper returned by useDBO. Encapsulates state and
 * a collection of convenience methods for reading and mutating an in-memory
 * array of items fetched from a remote endpoint.
 *
 * @template T - The item type stored in the collection. Several helpers expect
 *                 items to expose an "_id" string property (or "id" for some
 *                 helpers) when performing identity-based operations.
 *
 * Properties:
 * @property {T[] | undefined} get
 *   - Current array of items or undefined while data has not yet been loaded.
 *
 * @property {React.Dispatch<React.SetStateAction<T[] | undefined>>} set
 *   - React state setter for directly replacing or updating the underlying array.
 *
 * @property {any} error
 *   - Any error encountered during the last fetch operation.
 *
 * @property {boolean} loading
 *   - True while a fetch is in progress; false otherwise.
 *
 * Methods:
 * @property {(item: T) => void} add
 *   - Appends a single item to the current collection.
 *
 * @property {(id: string | (T & { _id: string })) => void} remove
 *   - Removes an item by its id or by passing the full item. If given an object,
 *     its "_id" is used; otherwise the provided string is treated as the id.
 *
 * @property {(item: T & { _id: string }) => void} update
 *   - Merges the provided fields into the existing item that matches "_id".
 *
 * @property {(id: string, item: Partial<T>) => void} updateById
 *   - Merges the provided partial fields into the item matching the given id.
 *
 * @property {(item: T & { id: string }) => void} updateId
 *   - Like update/updateById but operates on an "id" property instead of "_id".
 *
 * @property {(fn: (item: T) => boolean) => T[]} filter
 *   - Returns a new array containing items that satisfy the predicate function.
 *
 * @property {() => void} log
 *   - Logs the current collection to the console (useful for debugging).
 *
 * @property {(fn: (a: T, b: T) => number) => T[]} sort
 *   - Returns a new array sorted using the provided comparator.
 *
 * @property {(field: keyof T, direction?: number) => T[]} sortBy
 *   - Returns a new array sorted by the given field. direction should be 1 for
 *     ascending (default) or -1 for descending. Comparisons use JavaScript's
 *     > and < semantics.
 *
 * @property {() => Promise<void>} refresh
 *   - Re-fetches data from the server without toggling the loading indicator
 *     (useful for background refreshes).
 *
 * @property {(field: keyof T) => number} reduce
 *   - Sums numeric values of the specified field across all items. Fields are
 *     cast to number; non-numeric values may produce NaN.
 *
 * @property {(field: keyof T) => any[]} uniqueSet
 *   - Returns an array of unique values for the specified field across all items.
 *
 * @property {<U>(fn: (item: T) => U) => U[]} map
 *   - Maps the current collection to a new array using the provided mapper.
 *
 * @property {(fn: (item: T) => boolean) => T | undefined} find
 *   - Finds and returns the first item that satisfies the predicate, or undefined.
 *
 * @property {(id: string) => T | undefined} findById
 *   - Finds an item by its "_id" property and returns it or undefined.
 *
 * @property {(item: T & { _id: string }) => void} replace
 *   - Replaces the entire stored item (not merged) for the matching "_id".
 *
 * @property {() => number} length
 *   - Returns the number of items currently held (0 if undefined).
 *
 * Notes:
 * - Identity-based helpers (findById, remove, update, replace, updateById)
 *   assume items expose an "_id" string property unless otherwise noted.
 * - All array-returning helpers return new arrays and do not mutate the stored
 *   state; to persist changes to the internal state, use the provided methods
 *   that call the internal setter (add/remove/update/replace/updateById).
 */
 
/**
 * useDBO<T>(url: string, reLoad?: number, dependencies?: any[], loaded?: (data: T[]) => void): Usedbo<T>
 *
 * Custom React hook that fetches an array of items from a JSON API and provides
 * a rich set of utilities to operate on the local cached collection.
 *
 * @template T - The item type contained in the fetched array.
 *
 * @param url - The endpoint path (appended to the "/api" base path) to fetch the
 *              resource from. Should return an array of T.
 *
 * @param reLoad - Optional polling interval in seconds. If greater than 0, the
 *                 hook will re-fetch data at this interval. Default: 0 (no poll).
 *
 * @param dependencies - Optional array of values which, when changed, will
 *                       re-trigger the initial fetch and reset the polling
 *                       interval. These are merged into the hook's useEffect
 *                       dependency list.
 *
 * @param loaded - Optional callback invoked with the fetched data after a
 *                 successful response. Useful for side-effects external to the
 *                 hook's internal state.
 *
 * @returns {Usedbo<T>} An object containing:
 *  - Reactive state: get, set, loading, error
 *  - Mutators: add, remove, update, updateById, updateId, replace
 *  - Query helpers: filter, find, findById, map, sort, sortBy, uniqueSet, reduce
 *  - Utilities: refresh, log, length
 *
 * @remarks
 * - The hook performs a GET request to `/api${url}` using axios and includes an
 *   Authorization header (Bearer token) and JSON content-type. Network errors
 *   are captured in the returned `error` property.
 * - The initial fetch sets loading to true while in progress. Subsequent
 *   background refreshes (triggered by polling or refresh()) default to not
 *   toggle the loading flag when called with showLoading=false.
 *
 * @example
 * // const users = useDBO<User>('/users', 60, [filter], (data) => console.log('loaded', data));
 */
export default function useDBO<T>(
  url: string,
  reLoad = 0,
  dependencies: any[] = [],
  loaded?: (data: T[]) => void
): Usedbo<T> {
  const [get, set] = useState<T[] | undefined>(undefined);
  const [error, setError] = useState<any>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    getData(url);
    if (reLoad < 1) return;
    const interval = setInterval(() => {
      getData(url, false);
    }, reLoad * 1000);
    return () => clearInterval(interval);
  }, [url, reLoad, ...dependencies]);

  async function getData(url: string, showLoading = true) {
    try {
      if (showLoading) setLoading(true);
      const response = await axios.get(`/api${url}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + apiKey,
        },
      });
      set(response.data);
      if (loaded) loaded(response.data);
    } catch (err) {
      setError(err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }

  function add(item: T) {
    set([...(get || []), item]);
  }

  function length() {
    return get?.length || 0;
  }

  function log() {
    console.log(get);
  }

  async function refresh() {
    await getData(url, false);
  }

  function update(item: T & { _id: string }) {
    set((prev) =>
      prev?.map((i: any) => (i._id === item._id ? { ...i, ...item } : i))
    );
  }

  function replace(item: T & { _id: string }) {
    set((prev) => prev?.map((i: any) => (i._id === item._id ? item : i)));
  }

  function updateById(id: string, item: Partial<T>) {
    set(get?.map((i: any) => (i._id === id ? { ...i, ...item } : i)));
  }

  function updateId(item: T & { id: string }) {
    set(get?.map((i: any) => (i.id === item.id ? { ...i, ...item } : i)));
  }

  function remove(id: string | (T & { _id: string })) {
    if (typeof id === "object") {
      set(get?.filter((item: any) => item._id !== id._id));
    } else {
      set(get?.filter((item: any) => item._id !== id));
    }
  }

  function filter(fn: (item: T) => boolean) {
    return get?.filter(fn) || [];
  }

  function sort(fn: (a: T, b: T) => number) {
    return [...(get || [])].sort(fn);
  }

  function sortBy(field: keyof T, direction = 1) {
    return [...(get || [])].sort((a, b) =>
      a[field] > b[field] ? direction : b[field] > a[field] ? -direction : 0
    );
  }

  function reduce(field: keyof T): number {
    return (get || []).reduce((acc, item) => acc + (item[field] as number), 0);
  }

  function uniqueSet(field: keyof T) {
    return [...new Set((get || []).map((a) => a[field]))];
  }

  function map<U>(fn: (item: T) => U): U[] {
    return (get || []).map(fn);
  }

  function find(fn: (item: T) => boolean): T | undefined {
    return get?.find(fn);
  }

  function findById(id: string): T | undefined {
    return get?.find((i: any) => i._id === id);
  }

  return {
    get,
    set,
    error,
    loading,
    add,
    remove,
    update,
    updateById,
    updateId,
    filter,
    log,
    sort,
    sortBy,
    refresh,
    reduce,
    uniqueSet,
    map,
    find,
    findById,
    replace,
    length,
  };
}
